import { RootState } from "../../../app/store"
import { RoleName } from "../../auth/types"
import { useTypedSelector } from "../../../app/store"
import { useLocation, useParams } from "react-router-dom"
import React from "react"

type IconOrImage = {
  icon: boolean
  image: string | (() => React.ReactElement)
}

type MenuItem = {
  id: number
  name: string
  openIcon: IconOrImage
  closeIcon: IconOrImage
  path: string
  active: boolean
  roles: RoleName[]
  place: "top" | "bottom"
  activePathPrefix: string[]
  disable: boolean
  group?: string
}

type MenuGroup = {
  id: string
  label: string
  items: MenuItem[]
}

// Custom SVG icons - Meseekna brand colors
const HomeIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
)

const DeployIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
)

const TeamSimIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)

const ScoresIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
)

const BenchmarkIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
)

const DevelopIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
  </svg>
)

const WhoThinksIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const AIIntegrationIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)


const MeasuresIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
)

const AccountIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
)

const SupportIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const SimulationIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

// Super Admin specific icons
const DashboardIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
)

const AnalyticsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
)

const OrganizationsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
)

const AssignmentsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
)

const LiveActivityIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
  </svg>
)

const ApprovalsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const BuilderIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
  </svg>
)

const ReportsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)

const TutorialIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
)

const SearchIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
)

const MeasureAccessIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
)


const TeamMatcherIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
)

const TeamSetupIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
)

const TeamResultsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
)

const DatabaseIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
  </svg>
)

// My Development section icons
const OverviewIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
)

const JournalIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
)

const ScenariosIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
)

const SettingsIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const MeasureGuideIcon = (): JSX.Element => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
)

// Menu items organized for the new structure
const simulationMenuItems: MenuItem[] = [
  // =====================
  // SUPER ADMIN - DASHBOARD GROUP (alphabetical)
  // =====================
  {
    id: -17,
    name: "Search Platform",
    openIcon: { icon: true, image: SearchIcon },
    closeIcon: { icon: true, image: SearchIcon },
    path: "/dashboard/super-admin/data-explorer",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/data-explorer"],
    disable: false,
    group: "sa-dashboard",
  },
  {
    id: -12,
    name: "View Live Activity",
    openIcon: { icon: true, image: LiveActivityIcon },
    closeIcon: { icon: true, image: LiveActivityIcon },
    path: "/dashboard/super-admin/live-activity",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/live-activity"],
    disable: false,
    group: "sa-dashboard",
  },
  {
    id: -11,
    name: "View Platform Analytics",
    openIcon: { icon: true, image: AnalyticsIcon },
    closeIcon: { icon: true, image: AnalyticsIcon },
    path: "/dashboard/super-admin/analytics",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/analytics"],
    disable: false,
    group: "sa-dashboard",
  },
  {
    id: -10,
    name: "View Platform Overview",
    openIcon: { icon: true, image: DashboardIcon },
    closeIcon: { icon: true, image: DashboardIcon },
    path: "/dashboard/super-admin/overview",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/overview"],
    disable: false,
    group: "sa-dashboard",
  },

  // =====================
  // SUPER ADMIN - DEPLOYMENT MANAGEMENT GROUP (alphabetical)
  // =====================
  {
    id: -13,
    name: "Approve New Simulations",
    openIcon: { icon: true, image: ApprovalsIcon },
    closeIcon: { icon: true, image: ApprovalsIcon },
    path: "/dashboard/super-admin/pending-approvals",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/pending-approvals"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -22,
    name: "Approve Results Access",
    openIcon: { icon: true, image: ApprovalsIcon },
    closeIcon: { icon: true, image: ApprovalsIcon },
    path: "/dashboard/super-admin/pending-results-approvals",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/pending-results-approvals"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -31,
    name: "Browse Simulations",
    openIcon: { icon: true, image: SimulationIcon },
    closeIcon: { icon: true, image: SimulationIcon },
    path: "/dashboard/super-admin/simulations",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/simulations"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -33,
    name: "Edit Tutorials",
    openIcon: { icon: true, image: TutorialIcon },
    closeIcon: { icon: true, image: TutorialIcon },
    path: "/dashboard/super-admin/manage-simulations",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: [
      "/dashboard/super-admin/manage-simulations",
      "/dashboard/super-admin/sims",
    ],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -16,
    name: "Browse Measure Library",
    openIcon: { icon: true, image: MeasuresIcon },
    closeIcon: { icon: true, image: MeasuresIcon },
    path: "/dashboard/super-admin/measures",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/measures"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -4,
    name: "Create A Simulation",
    openIcon: { icon: true, image: BuilderIcon },
    closeIcon: { icon: true, image: BuilderIcon },
    path: "/simulation-creator",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/simulation-creator"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -21,
    name: "View Scores",
    openIcon: { icon: true, image: BenchmarkIcon },
    closeIcon: { icon: true, image: BenchmarkIcon },
    path: "/dashboard/super-admin/org-analytics",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/org-analytics"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -1,
    name: "Manage Client Organizations",
    openIcon: { icon: true, image: OrganizationsIcon },
    closeIcon: { icon: true, image: OrganizationsIcon },
    path: "/dashboard/super-admin",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -18,
    name: "Manage Org Measures",
    openIcon: { icon: true, image: MeasureAccessIcon },
    closeIcon: { icon: true, image: MeasureAccessIcon },
    path: "/dashboard/super-admin/org-measures",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/org-measures"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -20,
    name: "Review Team Simulation Results",
    openIcon: { icon: true, image: TeamResultsIcon },
    closeIcon: { icon: true, image: TeamResultsIcon },
    path: "/dashboard/super-admin/team-simulations",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/team-simulations"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -30,
    name: "View Raw Data",
    openIcon: { icon: true, image: DatabaseIcon },
    closeIcon: { icon: true, image: DatabaseIcon },
    path: "/dashboard/super-admin/raw-data",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/raw-data"],
    disable: false,
    group: "sa-deployment",
  },
  {
    id: -19,
    name: "View Simulation Assignments",
    openIcon: { icon: true, image: AssignmentsIcon },
    closeIcon: { icon: true, image: AssignmentsIcon },
    path: "/dashboard/super-admin/all-simulations",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/all-simulations"],
    disable: false,
    group: "sa-deployment",
  },

  // =====================
  // SUPER ADMIN - ANALYTICS GROUP (alphabetical)
  // =====================
  {
    id: -32,
    name: "Manage Score Sets",
    openIcon: { icon: true, image: ScoresIcon },
    closeIcon: { icon: true, image: ScoresIcon },
    path: "/dashboard/super-admin/score-sets",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/score-sets"],
    disable: false,
    group: "sa-analytics",
  },
  {
    id: -14,
    name: "View Organization Reports",
    openIcon: { icon: true, image: ReportsIcon },
    closeIcon: { icon: true, image: ReportsIcon },
    path: "/dashboard/super-admin/org-reports",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/org-reports"],
    disable: false,
    group: "sa-analytics",
  },
  {
    id: -15,
    name: "View Simulation Reports",
    openIcon: { icon: true, image: ReportsIcon },
    closeIcon: { icon: true, image: ReportsIcon },
    path: "/dashboard/super-admin/sim-reports",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/dashboard/super-admin/sim-reports"],
    disable: false,
    group: "sa-analytics",
  },

  // =====================
  // SUPER ADMIN - CLIENT TOOLS GROUP (alphabetical)
  // =====================
  {
    id: -2,
    name: "Find Who Thinks Like This",
    openIcon: { icon: true, image: TeamMatcherIcon },
    closeIcon: { icon: true, image: TeamMatcherIcon },
    path: "/chatbot",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/chatbot"],
    disable: false,
    group: "sa-client-tools",
  },
  {
    id: -3,
    name: "Set Up Team Sim",
    openIcon: { icon: true, image: TeamSetupIcon },
    closeIcon: { icon: true, image: TeamSetupIcon },
    path: "/admin/team-simulations/setup",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/admin/team-simulations"],
    disable: false,
    group: "sa-client-tools",
  },

  // =====================
  // SUPER ADMIN - ACCOUNT GROUP
  // =====================
  {
    id: -27,
    name: "Manage Settings",
    openIcon: { icon: true, image: AccountIcon },
    closeIcon: { icon: true, image: AccountIcon },
    path: "/account",
    active: false,
    roles: [RoleName.SiteAdmin],
    place: "top",
    activePathPrefix: ["/account"],
    disable: false,
    group: "sa-account",
  },
  // =====================
  // ADMIN - Home (standalone)
  // =====================
  {
    id: 10,
    name: "Home",
    openIcon: { icon: true, image: HomeIcon },
    closeIcon: { icon: true, image: HomeIcon },
    path: "/dashboard/recruiter-admin",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/welcome"],
    disable: false,
    group: "home",
  },
  {
    id: 23,
    name: "My Simulations",
    openIcon: { icon: true, image: SimulationIcon },
    closeIcon: { icon: true, image: SimulationIcon },
    path: "/dashboard/simulations",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/simulations", "/simulations"],
    disable: false,
    group: "home",
  },
  // =====================
  // ADMIN - ASSESS group
  // =====================
  {
    id: 11,
    name: "Deploy Simulation",
    openIcon: { icon: true, image: DeployIcon },
    closeIcon: { icon: true, image: DeployIcon },
    path: "/dashboard/recruiter-admin/users/all",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/users"],
    disable: false,
    group: "assess",
  },
  {
    id: 12,
    name: "Team Simulations",
    openIcon: { icon: true, image: TeamSimIcon },
    closeIcon: { icon: true, image: TeamSimIcon },
    path: "/dashboard/recruiter-admin/team-simulations",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/team-simulations", "/admin/team-simulations"],
    disable: false,
    group: "assess",
  },

  // =====================
  // ADMIN - REVIEW group
  // =====================
  {
    id: 13,
    name: "Cognitive Blueprints",
    openIcon: { icon: true, image: ScoresIcon },
    closeIcon: { icon: true, image: ScoresIcon },
    path: "/dashboard/recruiter-admin/scores",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/scores"],
    disable: false,
    group: "review",
  },
  {
    id: 14,
    name: "Benchmarks",
    openIcon: { icon: true, image: BenchmarkIcon },
    closeIcon: { icon: true, image: BenchmarkIcon },
    path: "/dashboard/recruiter-admin/benchmarks",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/benchmarks"],
    disable: false,
    group: "review",
  },
  // =====================
  // ADMIN - GROW group
  // =====================
  {
    id: 16,
    name: "Develop",
    openIcon: { icon: true, image: DevelopIcon },
    closeIcon: { icon: true, image: DevelopIcon },
    path: "/dashboard/recruiter-admin/microlearning",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/microlearning"],
    disable: false,
    group: "grow",
  },
  {
    id: 17,
    name: "Who Thinks Like This?",
    openIcon: { icon: true, image: WhoThinksIcon },
    closeIcon: { icon: true, image: WhoThinksIcon },
    path: "/dashboard/recruiter-admin/who-thinks",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/who-thinks"],
    disable: false,
    group: "grow",
  },
  {
    id: 23,
    name: "AI Integration",
    openIcon: { icon: true, image: AIIntegrationIcon },
    closeIcon: { icon: true, image: AIIntegrationIcon },
    path: "/dashboard/recruiter-admin/ai-integration",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/ai-integration"],
    disable: false,
    group: "grow",
  },

  // =====================
  // ADMIN - MANAGE group
  // =====================
  // Note: Users and Groups have been moved to tabs within Deploy Simulation page
  // Keeping IDs 18, 19 reserved for future use
  {
    id: 20,
    name: "Measures",
    openIcon: { icon: true, image: MeasuresIcon },
    closeIcon: { icon: true, image: MeasuresIcon },
    path: "/dashboard/recruiter-admin/measures",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/measures"],
    disable: false,
    group: "manage",
  },
  {
    id: 21,
    name: "Account",
    openIcon: { icon: true, image: AccountIcon },
    closeIcon: { icon: true, image: AccountIcon },
    path: "/account",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/account"],
    disable: false,
    group: "manage",
  },
  {
    id: 22,
    name: "Support",
    openIcon: { icon: true, image: SupportIcon },
    closeIcon: { icon: true, image: SupportIcon },
    path: "/support",
    active: false,
    roles: [RoleName.Admin],
    place: "top",
    activePathPrefix: ["/support"],
    disable: false,
    group: "manage",
  },

  // =====================
  // TEAM MEMBER items (for non-admin users) - with groups
  // =====================
  {
    id: 2,
    name: "My Simulation",
    openIcon: { icon: true, image: SimulationIcon },
    closeIcon: { icon: true, image: SimulationIcon },
    path: "/dashboard/simulations",
    place: "top",
    active: true,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/dashboard/simulations", "/simulations"],
    disable: false,
    group: "teammember-simulation",
  },
  // My Development section - Overview, Journal, Scenarios, Settings (only for TeamMember)
  {
    id: 100,
    name: "Overview",
    openIcon: { icon: true, image: OverviewIcon },
    closeIcon: { icon: true, image: OverviewIcon },
    path: "/microlearning/overview",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/microlearning/overview", "/microlearning"],
    group: "teammember-development",
  },
  {
    id: 101,
    name: "Journal",
    openIcon: { icon: true, image: JournalIcon },
    closeIcon: { icon: true, image: JournalIcon },
    path: "/microlearning/journal",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/microlearning/journal"],
    group: "teammember-development",
  },
  {
    id: 102,
    name: "Scenarios",
    openIcon: { icon: true, image: ScenariosIcon },
    closeIcon: { icon: true, image: ScenariosIcon },
    path: "/microlearning/scenarios",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/microlearning/scenarios"],
    group: "teammember-development",
  },
  {
    id: 103,
    name: "Preferences",
    openIcon: { icon: true, image: SettingsIcon },
    closeIcon: { icon: true, image: SettingsIcon },
    path: "/microlearning/settings",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/microlearning/settings"],
    group: "teammember-development",
  },
  {
    id: 104,
    name: "Measure Guide",
    openIcon: { icon: true, image: MeasureGuideIcon },
    closeIcon: { icon: true, image: MeasureGuideIcon },
    path: "/measure-guide",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/measure-guide"],
    group: "teammember-development",
  },
  // TeamMember Support and Account menu items
  {
    id: 30,
    name: "Support",
    openIcon: { icon: true, image: SupportIcon },
    closeIcon: { icon: true, image: SupportIcon },
    path: "/support",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/support"],
    group: "teammember-account",
  },
  {
    id: 31,
    name: "Account",
    openIcon: { icon: true, image: AccountIcon },
    closeIcon: { icon: true, image: AccountIcon },
    path: "/account",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.TeamMember],
    activePathPrefix: ["/account"],
    group: "teammember-account",
  },
  // =====================
  // SIMULATION USER (Candidate) items - flat navigation, no development section
  // =====================
  {
    id: 200,
    name: "My Simulation",
    openIcon: { icon: true, image: SimulationIcon },
    closeIcon: { icon: true, image: SimulationIcon },
    path: "/dashboard/simulations",
    place: "top",
    active: true,
    roles: [RoleName.SimulationUser],
    activePathPrefix: ["/dashboard/simulations", "/simulations"],
    disable: false,
  },
  {
    id: 201,
    name: "Support",
    openIcon: { icon: true, image: SupportIcon },
    closeIcon: { icon: true, image: SupportIcon },
    path: "/support",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.SimulationUser],
    activePathPrefix: ["/support"],
  },
  {
    id: 202,
    name: "Account",
    openIcon: { icon: true, image: AccountIcon },
    closeIcon: { icon: true, image: AccountIcon },
    path: "/account",
    place: "top",
    active: false,
    disable: false,
    roles: [RoleName.SimulationUser],
    activePathPrefix: ["/account"],
  },

  // =====================
  // RECRUITER (role 3) - Deploy, Results, Support, Account
  // =====================
  {
    id: 40,
    name: "Home",
    openIcon: { icon: true, image: HomeIcon },
    closeIcon: { icon: true, image: HomeIcon },
    path: "/dashboard/recruiter",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter", "/dashboard/recruiter/welcome"],
    disable: false,
    group: "recruiter-home",
  },
  {
    id: 41,
    name: "Deploy Simulation",
    openIcon: { icon: true, image: DeployIcon },
    closeIcon: { icon: true, image: DeployIcon },
    path: "/dashboard/recruiter-admin/users/all",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/users"],
    disable: false,
    group: "recruiter-assess",
  },
  {
    id: 42,
    name: "Cognitive Blueprints",
    openIcon: { icon: true, image: ScoresIcon },
    closeIcon: { icon: true, image: ScoresIcon },
    path: "/dashboard/recruiter-admin/scores",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/scores"],
    disable: false,
    group: "recruiter-review",
  },
  {
    id: 43,
    name: "Benchmarks",
    openIcon: { icon: true, image: BenchmarkIcon },
    closeIcon: { icon: true, image: BenchmarkIcon },
    path: "/dashboard/recruiter-admin/benchmarks",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/dashboard/recruiter-admin/benchmarks"],
    disable: false,
    group: "recruiter-review",
  },
  {
    id: 45,
    name: "Account",
    openIcon: { icon: true, image: AccountIcon },
    closeIcon: { icon: true, image: AccountIcon },
    path: "/account",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/account"],
    disable: false,
    group: "recruiter-manage",
  },
  {
    id: 46,
    name: "Support",
    openIcon: { icon: true, image: SupportIcon },
    closeIcon: { icon: true, image: SupportIcon },
    path: "/support",
    active: false,
    roles: [RoleName.Recruiter],
    place: "top",
    activePathPrefix: ["/support"],
    disable: false,
    group: "recruiter-manage",
  },
]

// Group definitions for Admin users
const adminMenuGroups: { id: string; label: string }[] = [
  { id: "home", label: "" },
  { id: "assess", label: "ANALYZE" },
  { id: "review", label: "REVIEW" },
  { id: "grow", label: "DEVELOP" },
  { id: "manage", label: "MANAGE" },
]

// Group definitions for Super Admin users
const superAdminMenuGroups: { id: string; label: string }[] = [
  { id: "sa-dashboard", label: "DASHBOARD" },
  { id: "sa-deployment", label: "DEPLOYMENT MANAGEMENT" },
  { id: "sa-analytics", label: "ANALYTICS" },
  { id: "sa-client-tools", label: "CLIENT TOOLS" },
  { id: "sa-account", label: "ACCOUNT" },
]

// Group definitions for Recruiter users (role 3)
const recruiterMenuGroups: { id: string; label: string }[] = [
  { id: "recruiter-home", label: "" },
  { id: "recruiter-assess", label: "ANALYZE" },
  { id: "recruiter-review", label: "REVIEW" },
  { id: "recruiter-manage", label: "MANAGE" },
]

// Group definitions for TeamMember users (role 4) - with My Development section
const teamMemberMenuGroups: { id: string; label: string }[] = [
  { id: "teammember-simulation", label: "" },
  { id: "teammember-development", label: "MY DEVELOPMENT" },
  { id: "teammember-account", label: "" },
]

// Group definitions for Candidate/SimulationUser users (role 5) - no development section
const candidateMenuGroups: { id: string; label: string }[] = [
  { id: "candidate-manage", label: "" },
]

const useMenuItems = () => {
  const { testId, runId } = useParams()

  const user = useTypedSelector((state: RootState) => {
    return state.authSlice.user
  })

  const role = useTypedSelector((state: RootState) => {
    return state.authSlice.role
  })

  // Check if user is a SiteAdmin (superadmin)
  const isSiteAdmin =
    user?.roles?.some((r) => r.roleId === RoleName.SiteAdmin) ||
    user?.role === RoleName.SiteAdmin

  const menuItems = simulationMenuItems.filter((item) =>
    item.roles.some(
      (roleId) =>
        user?.roles?.some((role) => role.roleId === roleId) ||
        user?.role === roleId,
    ),
  )

  // set the active menu item by comparing the current path with the activePathPrefix
  const currentPath = useLocation().pathname

  // For team member we are disabling overview and progress tabs as selection of micro learning is completed
  menuItems.map((item) => {
    if (role.roleId == 4 && !isSiteAdmin) {
      if (
        currentPath.includes("overView") ||
        currentPath.includes("progress")
      ) {
        if (item.id === 7 || item.id === 8) {
          item.path =
            item.id == 7
              ? `/dashboard/${testId}/${runId}/overView`
              : `/dashboard/${testId}/${runId}/progress`
          item.disable = false
        }
      }

      if (
        "/dashboard/simulations" === currentPath ||
        "/dashboard" === currentPath
      ) {
        if (item.id === 7 || item.id === 8) {
          item.disable = true
        } else if (
          item.path === "/dashboard/simulations" ||
          item.path === "/dashboard"
        ) {
          item.disable = false
        }
      }
    }

    // Check for exact match on home path (Admin)
    if (item.id === 10 && currentPath === "/dashboard/recruiter-admin") {
      item.active = true
    // Check for exact match on organizations path (Super Admin)
    } else if (item.id === -1 && currentPath === "/dashboard/super-admin" && !currentPath.includes("/dashboard/super-admin/")) {
      item.active = true
    } else if (
      item.activePathPrefix?.some((prefix) => currentPath.startsWith(prefix))
    ) {
      item.active = true
    } else {
      currentPath.includes("overView") && item.id === 7
        ? (item.active = true)
        : currentPath.includes("progress") && item.id === 8
          ? (item.active = true)
          : (item.active = false)
    }
  })

  return menuItems
}

const useMenuGroups = (): MenuGroup[] => {
  const menuItems = useMenuItems()
  const user = useTypedSelector((state: RootState) => state.authSlice.user)

  // Check if user is a SiteAdmin (superadmin)
  const isSiteAdmin =
    user?.roles?.some((r) => r.roleId === RoleName.SiteAdmin) ||
    user?.role === RoleName.SiteAdmin

  // Check if user is an Admin (Recruiter Admin - role 8)
  const isAdmin =
    user?.roles?.some((r) => r.roleId === RoleName.Admin) ||
    user?.role === RoleName.Admin

  // Check if user is a Recruiter (role 3)
  const isRecruiter =
    user?.roles?.some((r) => r.roleId === RoleName.Recruiter) ||
    user?.role === RoleName.Recruiter

  // Check if user is a TeamMember (role 4) - has My Development section
  const isTeamMember =
    user?.roles?.some((r) => r.roleId === RoleName.TeamMember) ||
    user?.role === RoleName.TeamMember

  // Check if user is a Candidate/SimulationUser (role 5) - no development section
  const isCandidate =
    user?.roles?.some((r) => r.roleId === RoleName.SimulationUser) ||
    user?.role === RoleName.SimulationUser

  // Use appropriate group definitions based on role (check in order of precedence)
  const menuGroups = isSiteAdmin
    ? superAdminMenuGroups
    : isAdmin
      ? adminMenuGroups
      : isRecruiter
        ? recruiterMenuGroups
        : isTeamMember
          ? teamMemberMenuGroups
          : isCandidate
            ? candidateMenuGroups
            : []

  // Filter to items with groups for the appropriate role
  const groupedItems = menuItems.filter(item => item.group)

  return menuGroups.map(group => ({
    id: group.id,
    label: group.label,
    items: groupedItems.filter(item => item.group === group.id)
  })).filter(group => group.items.length > 0)
}

export { simulationMenuItems, adminMenuGroups, superAdminMenuGroups, recruiterMenuGroups, teamMemberMenuGroups, candidateMenuGroups, type MenuItem, type MenuGroup, type IconOrImage, useMenuItems, useMenuGroups }
